'use client';

/**
 * WelcomePopup — modal de bienvenida obligatorio para nuevos usuarios.
 *
 * Feature (v136 — Founder directive 2026-11-04): "cuando un usuario se
 * registra, la primera vez que accede le aparezca un pop up que no pueda
 * cerrar y le de la bienvenida y lo lleve a crear su tarjeta digital".
 *
 * Flow:
 *   1. Al montar el AppShell, verificar si el usuario tiene profile.name
 *      (la tarjeta digital). Si NO lo tiene, mostrar el popup.
 *   2. El popup NO se puede cerrar (no hay botón X, no se puede click fuera).
 *   3. El único botón es "Ir a Profileia Hub" que redirige a /app.
 *   4. Usamos localStorage flag 'profileia.welcomeShown' para no mostrar
 *      el popup cada vez que el usuario entra (solo la primera vez).
 *   5. Si el usuario YA tiene profile.name, no mostrar el popup.
 */

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { Route } from 'next';
import { Sparkles, ArrowRight, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface WelcomePopupProps {
  hasProfileName: boolean;
  userId: string;
}

export function WelcomePopup({ hasProfileName, userId }: WelcomePopupProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // v136 fix: el popup se sigue mostrando hasta que el usuario tenga
    // profile.name set. NO usar localStorage flag — el Founder directive dice
    // "no vuelve a aparecer aunque no haya editado la tarjeta, me dijiste que
    // le seguiría apareciendo hasta que colocara su nombre de perfil".
    // Fix: si hasProfileName es false, mostrar el popup SIEMPRE (no hay flag
    // de localStorage que lo oculte).
    if (!hasProfileName) {
      // Pequeño delay para que el AppShell termine de montar
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
    setOpen(false);
  }, [hasProfileName]);

  const handleGoToHub = () => {
    setOpen(false);
    router.push('/app/profile' as Route);
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* NO cerrar al click fuera */ }}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        showCloseButton={false}
      >
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <div className="rounded-full bg-primary/10 p-3">
              <Sparkles className="size-8 text-primary" aria-hidden="true" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            Te damos la bienvenida a Profileia
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-center px-2">
          <p className="text-sm font-medium text-foreground">
            Ahora tu emprendimiento tiene un hogar
          </p>
          <p className="text-sm text-muted-foreground">
            El primer paso en nuestro ecosistema es crear tu{' '}
            <strong className="text-foreground">Perfil inteligente</strong>.
            Por eso te invitamos a dar click a este botón{' '}
            <strong className="text-foreground">Ir a Profileia Hub</strong>{' '}
            que te llevará a hacerlo, es muy sencillo y en 5 minutos tendrás
            una tarjeta profesional y totalmente gratis para compartir con tus
            clientes y amigos.
          </p>
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <MessageCircle className="size-3.5" aria-hidden="true" />
            También hemos habilitado un chat con IA para que te ayude, si tienes alguna duda
          </p>
        </div>

        <div className="pt-2">
          <Button
            className="w-full"
            size="lg"
            onClick={handleGoToHub}
          >
            Ir a Profileia Hub
            <ArrowRight className="size-4 ml-2" aria-hidden="true" />
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground pt-1">
          ¡Buena suerte! 🚀
        </p>
      </DialogContent>
    </Dialog>
  );
}
